/**
 * Calendar-popover portal positioning (027 owner residuals r1, T066).
 *
 * The proto portals its calendar over the wizard footer so the full month is
 * ALWAYS visible; our plain popover (no new deps) does the equivalent with a
 * panel portaled to the wizard dialog and positioned absolutely against it.
 * This module is the PURE decision — geometry in, coordinates out — kept
 * free of DOM access so jsdom (which does no layout) can unit-test the
 * decision table while the component test covers only the wiring.
 *
 * Rules (within the dialog's box, which the panel may overlay entirely —
 * footer and progress area included):
 *   1. The panel opens BELOW the trigger (the proto's default direction)
 *      when the month grid fits between the trigger and the dialog bottom.
 *   2. It opens ABOVE only when below would clip the dialog bottom and the
 *      space above fits.
 *   3. When neither side fits (short phones, a mid-dialog field) the LARGER
 *      side wins and the panel is then shifted back inside the dialog,
 *      covering part of the trigger row, so the whole grid stays visible.
 *   4. A dialog shorter than the panel (a phone in landscape) caps the
 *      panel's height to the dialog minus its insets; the panel then scrolls.
 */

export type PanelPlacement = 'above' | 'below';

/** The geometry slice the decision needs (viewport coordinates). */
export interface RectLike {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

export interface PortalPositionInput {
  /** The calendar trigger button's rect. */
  readonly triggerRect: RectLike;
  /** The portal host's rect (the wizard dialog) — also the clip bounds. */
  readonly boundsRect: RectLike;
  /** The panel's outer height. */
  readonly panelHeight: number;
  /** The panel's width. */
  readonly panelWidth: number;
}

/** Absolute offsets WITHIN the portal host, plus the side that was chosen. */
export interface PanelPosition {
  readonly top: number;
  readonly left: number;
  /** The panel's width, capped so a host narrower than the panel keeps its insets. */
  readonly width: number;
  /** The panel's height cap, below its natural height only on a host shorter than the panel. */
  readonly maxHeight: number;
  readonly placement: PanelPlacement;
}

/** Gap between the trigger and the panel (the anchoring `mt-2` equivalent). */
const ANCHOR_GAP = 8;
/** Minimum inset from the host's edges. */
const EDGE_INSET = 8;

export function resolvePortalPosition({
  triggerRect,
  boundsRect,
  panelHeight,
  panelWidth,
}: PortalPositionInput): PanelPosition {
  const hostHeight = boundsRect.bottom - boundsRect.top;
  const maxHeight = Math.max(0, Math.min(panelHeight, hostHeight - 2 * EDGE_INSET));
  const spaceBelow = boundsRect.bottom - triggerRect.bottom - ANCHOR_GAP;
  const spaceAbove = triggerRect.top - boundsRect.top - ANCHOR_GAP;
  const placement: PanelPlacement =
    spaceBelow >= panelHeight
      ? 'below'
      : spaceAbove >= panelHeight
        ? 'above'
        : spaceBelow >= spaceAbove
          ? 'below'
          : 'above';
  const anchoredTop =
    placement === 'below'
      ? triggerRect.bottom - boundsRect.top + ANCHOR_GAP
      : triggerRect.top - boundsRect.top - ANCHOR_GAP - maxHeight;
  const maxTop = hostHeight - maxHeight - EDGE_INSET;
  const top = Math.max(EDGE_INSET, Math.min(anchoredTop, maxTop));
  // Right-align the panel to the trigger, clamped SYMMETRICALLY inside the
  // host: never past the left inset, never past the right inset. On a host
  // narrower than panel + insets the panel narrows to fit (a 320px phone).
  const hostWidth = boundsRect.right - boundsRect.left;
  const width = Math.max(0, Math.min(panelWidth, hostWidth - 2 * EDGE_INSET));
  const maxLeft = hostWidth - width - EDGE_INSET;
  const left = Math.max(EDGE_INSET, Math.min(triggerRect.right - boundsRect.left - width, maxLeft));
  return { top, left, width, maxHeight, placement };
}

// The panel's vertical anatomy, derived from CalendarPopover's classes —
// kept HERE so the estimate and the styles drift together visibly in review:
// border y (1+1) + p-4 y (16+16) + month row (h-11 + pb-3 = 44 + 12) +
// weekday row (h-6 + pb-1 = 32 + 4). `h-6` is 32px, not Tailwind's 24px:
// tokens.css retunes `--spacing-6` to 2rem.
const PANEL_BORDER = 2;
const PANEL_FIXED_HEIGHT = PANEL_BORDER + 32 + 56 + 36;
/** Day cell h-9 (36) + the grid's compact gap-y-0.5 (2) between rows. */
const DAY_ROW_HEIGHT = 36;
const DAY_ROW_GAP = 2;
/**
 * Every month renders this many Monday-first week rows (padded with
 * adjacent-month days), so the panel height never changes between months.
 */
export const CALENDAR_WEEK_ROWS = 6;
/** The panel's `w-84`, border included: room for the longest month name in WebKit's select. */
export const PANEL_WIDTH = 336;
/** The panel's outer height at a 16px root font size, the same in every month. */
export const PANEL_HEIGHT =
  PANEL_FIXED_HEIGHT + CALENDAR_WEEK_ROWS * DAY_ROW_HEIGHT + (CALENDAR_WEEK_ROWS - 1) * DAY_ROW_GAP;

/**
 * The panel's outer size at the root font size the member has chosen: every
 * length but the 1px border is in rem, so the panel grows with the font.
 */
export function panelSize(rootFontSize: number): { height: number; width: number } {
  const scale = Number.isFinite(rootFontSize) && rootFontSize > 0 ? rootFontSize / 16 : 1;
  return {
    height: PANEL_BORDER + (PANEL_HEIGHT - PANEL_BORDER) * scale,
    width: PANEL_WIDTH * scale,
  };
}
